import { c as _c } from "react/compiler-runtime";
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CheckCircleIcon, LectureStatusIcon } from '@systemeio/ui/icons';
import { Tooltip } from '@systemeio/ui';
import { LectureId } from 'modules/courses/courses/curriculum/types/lecture-interface';
import { nonDraggableProps } from 'modules/courses/courses/curriculum/utils/non-draggable-props';
import React from 'react';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { twMerge } from 'tailwind-merge';
import { getPageEditorUrl } from 'shared/utils/get-page-editor-url';
interface ModuleLectureProps {
  lecture: {
    id: LectureId;
    name: string;
    active: boolean;
    pageId?: number;
    delayBeforePreviousLecture?: number;
    hasAssignmentSettings?: boolean;
    assignmentActivatedAt?: string | null;
  };
  disabled: boolean;
  isLastChild: boolean;
  isFetching?: boolean;
  lectureActions: React.ReactNode;
  onNameClick?: React.MouseEventHandler<HTMLAnchorElement | HTMLDivElement | HTMLButtonElement>;
}
export function ModuleLecture(t0) {
  const $ = _c(64);
  const {
    lecture,
    disabled,
    isLastChild,
    isFetching,
    lectureActions,
    onNameClick
  } = t0;
  const {
    t
  } = useLocoTranslation();
  let t1;
  if ($[0] !== lecture.id) {
    t1 = {
      id: lecture.id
    };
    $[0] = lecture.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    setNodeRef,
    listeners,
    transform,
    transition
  } = useSortable(t1);
  let t2;
  if ($[2] !== transform) {
    t2 = CSS.Transform.toString(transform);
    $[2] = transform;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2 || $[5] !== transition) {
    t3 = {
      transform: t2,
      transition
    };
    $[4] = t2;
    $[5] = transition;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const style = t3;
  const t4 = isLastChild && "rounded-b-lg";
  const t5 = disabled && "cursor-not-allowed";
  const t6 = isFetching && "animate-pulse";
  let t7;
  if ($[7] !== t4 || $[8] !== t5 || $[9] !== t6) {
    t7 = twMerge("flex flex-col ", t4, "even:bg-brand-primary-subtle odd:bg-background-secondary items-start gap-4 lg:flex-row lg:items-center lg:gap-0", "hover:border-brand-primary main-transition-colors cursor-move border border-transparent", t5, t6);
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = disabled ? undefined : setNodeRef;
  let t9;
  if ($[11] !== lecture.name || $[12] !== lecture.pageId || $[13] !== onNameClick) {
    t9 = <div className="hidden w-full flex-[55_1_0%] truncate px-4 py-4 lg:block lg:px-6"><div className="flex items-center gap-3">{lecture.pageId ? <a className="primary-link cursor-pointer truncate text-sm font-normal" {...nonDraggableProps} href={getPageEditorUrl(lecture.pageId)} onClick={onNameClick} data-test="module-lecture-link">{lecture.name}</a> : <button className="primary-link cursor-pointer truncate text-sm font-normal" {...nonDraggableProps} onClick={onNameClick} data-test="module-lecture-name">{lecture.name}</button>}</div></div>;
    $[11] = lecture.name;
    $[12] = lecture.pageId;
    $[13] = onNameClick;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== lecture.name || $[16] !== onNameClick) {
    t10 = <div className="text-brand-primary flex-[87_1_0%] cursor-default truncate text-sm font-normal" {...nonDraggableProps} onClick={onNameClick} data-test="module-lecture-name">{lecture.name}</div>;
    $[15] = lecture.name;
    $[16] = onNameClick;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== lecture.active || $[19] !== t) {
    t11 = lecture.active ? t("global.active") : t("global.inactive");
    $[18] = lecture.active;
    $[19] = t;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const t12 = `w-[20px]h-[20px] ${lecture.active && "fill-green"}`;
  let t13;
  if ($[21] !== t12) {
    t13 = <CheckCircleIcon className={t12} />;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t11 || $[24] !== t13) {
    t14 = <span className="text-text-primary whitespace-nowrap font-bold"><div className="flex cursor-default items-center lg:ml-4" {...nonDraggableProps}><Tooltip label={t11} mode="hover" key="remove-tooltip">{t13}</Tooltip></div></span>;
    $[23] = t11;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== lecture.assignmentActivatedAt || $[27] !== lecture.hasAssignmentSettings || $[28] !== t) {
    t15 = lecture.hasAssignmentSettings && <Tooltip label={lecture.assignmentActivatedAt ? t("dashboard.course.lecture_assignment_active") : t("dashboard.course.lecture_assignment_inactive")} mode="hover"><LectureStatusIcon className={twMerge("h-[20px] w-[20px]", lecture.assignmentActivatedAt ? "fill-brand-primary" : "fill-fill-primary")} /></Tooltip>;
    $[26] = lecture.assignmentActivatedAt;
    $[27] = lecture.hasAssignmentSettings;
    $[28] = t;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t14 || $[31] !== t15) {
    t16 = <div className="flex flex-[15_1_0%] items-center  justify-between gap-3">{t14}{t15}</div>;
    $[30] = t14;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t10 || $[34] !== t16) {
    t17 = <div className="block w-full truncate px-4 py-2  lg:hidden lg:px-6"><div className="flex items-center gap-3 ">{t10}{t16}</div></div>;
    $[33] = t10;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== lecture.delayBeforePreviousLecture || $[37] !== t) {
    t18 = lecture.delayBeforePreviousLecture !== undefined && <div className="text-text-primary w-full flex-[15_1_0%] truncate px-4 py-2 text-sm font-normal lg:block lg:px-6"><div className="flex flex-col gap-1"><span data-test="module-lecture-delay">{lecture.delayBeforePreviousLecture === 0 ? t("dashboard.course.lecture_grant_access_immediately") : t("dashboard.course.lecture_days_delay_title", {
            daysCount: lecture.delayBeforePreviousLecture
          })}</span><span className="opacity-60">({t("dashboard.course.lecture_days_delay_subtitle")})</span></div></div>;
    $[36] = lecture.delayBeforePreviousLecture;
    $[37] = t;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== lecture.active || $[40] !== t) {
    t19 = lecture.active ? t("global.active") : t("global.inactive");
    $[39] = lecture.active;
    $[40] = t;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  const t20 = `h-[16px] w-[16px] sm:h-[20px] sm:w-[20px] ${lecture.active && "fill-green"}`;
  let t21;
  if ($[42] !== t20) {
    t21 = <CheckCircleIcon className={t20} />;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t19 || $[45] !== t21) {
    t22 = <div className="hidden w-full flex-[10_1_0%] truncate px-4 py-2 lg:block lg:px-6 lg:py-4"><div className="flex items-center gap-3"><span className="text-text-primary whitespace-nowrap font-bold"><div className="flex cursor-default items-center lg:ml-4" {...nonDraggableProps} data-test="module-lecture-status"><Tooltip label={t19} mode="hover" key="remove-tooltip">{t21}</Tooltip></div></span></div></div>;
    $[44] = t19;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== lecture.assignmentActivatedAt || $[48] !== lecture.hasAssignmentSettings || $[49] !== t) {
    t23 = lecture.hasAssignmentSettings && <Tooltip wrapperClassName="lg:block hidden" label={lecture.assignmentActivatedAt ? t("dashboard.course.lecture_assignment_active") : t("dashboard.course.lecture_assignment_inactive")} mode="hover"><LectureStatusIcon className={twMerge("h-[20px] w-[20px]", lecture.assignmentActivatedAt ? "fill-brand-primary" : "fill-fill-primary")} /></Tooltip>;
    $[47] = lecture.assignmentActivatedAt;
    $[48] = lecture.hasAssignmentSettings;
    $[49] = t;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== lectureActions || $[52] !== t23) {
    t24 = <div className="flex flex-[10_1_0%] items-center justify-end gap-6 px-4 py-4 lg:px-6">{t23}{lectureActions}</div>;
    $[51] = lectureActions;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== listeners || $[55] !== style || $[56] !== t17 || $[57] !== t18 || $[58] !== t22 || $[59] !== t24 || $[60] !== t7 || $[61] !== t8 || $[62] !== t9) {
    t25 = <div className={t7} style={style} ref={t8} {...listeners}>{t9}{t17}{t18}{t22}{t24}</div>;
    $[54] = listeners;
    $[55] = style;
    $[56] = t17;
    $[57] = t18;
    $[58] = t22;
    $[59] = t24;
    $[60] = t7;
    $[61] = t8;
    $[62] = t9;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  return t25;
}